import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import QRCodeViewerModal from "./QRCodeViewerModal";

const CustomerTable = ({
  customers,
  onEdit,
  onDelete,
  onSelect,
  selectedIds,
  deletingId,
  regions = [],
  clusters = [],
  teams = [],
  teamMembers = [],
  onAssignMember,
  onAssignCluster,
  onAssignStatus,
  onQuickAssignByRole,
  onOpenAssignRole,
  userRole,
  onDownloadQRCardsPDF,
  onExportCustomers,
  onBulkDelete,
  isBulkDeleting,
  onDownloadSurveyReport,
  onDownloadWorkCompletionReport,
  onDownloadPhotos,
  onBulkDownloadSurveyReports,
  onBulkDownloadWorkCompletionReports,
}) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [viewingQRCode, setViewingQRCode] = useState(null);
  const [rowActionSelection, setRowActionSelection] = useState({});
  const [reportDropdownOpen, setReportDropdownOpen] = useState(false);
  const reportDropdownRef = useRef(null);

  const isClusterManager = userRole === "cluster_manager";

  // Close report dropdown on click outside
  useEffect(() => {
    if (!reportDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        reportDropdownRef.current &&
        !reportDropdownRef.current.contains(e.target)
      ) {
        setReportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [reportDropdownOpen]);

  // Helper function to get team members for a specific team
  const getTeamMembersForTeam = (teamId) => {
    if (!teamId) return [];
    // Get team members directly where team_id matches
    return (teamMembers || []).filter((m) => m.team_id === teamId);
  };

  // Only show civil/technical members when stage is complete and approved
  const getAssignableTeamMembers = (customer) => {
    const list = getTeamMembersForTeam(customer?.team_id);
    if (!list.length) return list;
    const surveyCompleteOrLater = [
      "survey_complete",
      "survey_unworkable",
      "civil_pending",
      "civil_inprogress",
      "civil_complete",
      "technical_pending",
      "technical_inprogress",
      "technical_complete",
      "commissioning_not_started",
      "commissioning_inprogress",
      "commissioning_complete",
    ];
    const civilCompleteOrLater = [
      "civil_complete",
      "technical_pending",
      "technical_inprogress",
      "technical_complete",
    ];
    const surveyApproved =
      (customer?.survey_approval_status || "").toLowerCase() === "approved";
    const civilApproved =
      (customer?.civil_approval_status || "").toLowerCase() === "approved";
    const technicalApproved =
      (customer?.technical_approval_status || "").toLowerCase() === "approved";
    const technicalCompleteOrLater = [
      "technical_complete",
      "commissioning_not_started",
      "commissioning_inprogress",
      "commissioning_complete",
    ];
    return list.filter((m) => {
      if (m.role !== "worker") return false;
      return (
        (surveyCompleteOrLater.includes(customer?.status) && surveyApproved) ||
        (civilCompleteOrLater.includes(customer?.status) && civilApproved) ||
        (technicalCompleteOrLater.includes(customer?.status) &&
          technicalApproved) ||
        customer?.status === "survey_not_started" ||
        customer?.status === "survey_inprogress"
      );
    });
  };

  // Format role to human-readable text (e.g., "civil_worker" -> "Civil Worker")
  const formatRole = (role) => {
    if (!role) return "";
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig?.key === key && sortConfig?.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const sortedCustomers = React.useMemo(() => {
    if (!sortConfig?.key) return customers;

    return [...customers]?.sort((a, b) => {
      let aVal = a?.[sortConfig?.key];
      let bVal = b?.[sortConfig?.key];

      // Handle nested properties
      if (sortConfig?.key === "cluster") {
        aVal = a?.clusters?.name || "";
        bVal = b?.clusters?.name || "";
      } else if (sortConfig?.key === "region") {
        aVal = a?.regions?.name || "";
        bVal = b?.regions?.name || "";
      } else if (sortConfig?.key === "project") {
        aVal = a?.projects?.name || "";
        bVal = b?.projects?.name || "";
      }

      if (aVal < bVal) return sortConfig?.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig?.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [customers, sortConfig]);

  const statusOptions = [
    { value: "survey_not_started", label: "Survey Not Started" },
    { value: "survey_inprogress", label: "Survey In Progress" },
    { value: "survey_complete", label: "Survey Complete" },
    { value: "survey_unworkable", label: "Survey Unworkable" },
    { value: "transport_pending", label: "Transport Pending" },
    { value: "transport_inprogress", label: "Transport In Progress" },
    { value: "transport_complete", label: "Transport Complete" },
    { value: "civil_pending", label: "Civil Pending" },
    { value: "civil_inprogress", label: "Civil In Progress" },
    { value: "civil_complete", label: "Civil Complete" },
    { value: "technical_pending", label: "Technical Pending" },
    { value: "technical_inprogress", label: "Technical In Progress" },
    { value: "technical_complete", label: "Technical Complete" },
    {
      value: "commissioning_not_started",
      label: "Commissioning Not Started",
    },
    {
      value: "commissioning_inprogress",
      label: "Commissioning In Progress",
    },
    { value: "commissioning_complete", label: "Commissioning Complete" },
  ];

  // Row background and left border by status (vibrant color-coding)
  const getRowStatusClasses = (status) => {
    const rowStyles = {
      survey_not_started:
        "bg-white dark:bg-gray-900 border-l-4 border-l-gray-300 dark:border-l-gray-600",
      survey_inprogress:
        "bg-yellow-100 dark:bg-yellow-900/50 border-l-4 border-l-yellow-500",
      survey_complete:
        "bg-blue-100 dark:bg-blue-900/50 border-l-4 border-l-blue-500",
      survey_unworkable:
        "bg-red-100 dark:bg-red-900/50 border-l-4 border-l-red-500",
      civil_pending:
        "bg-yellow-100 dark:bg-yellow-900/50 border-l-4 border-l-yellow-500",
      civil_inprogress:
        "bg-yellow-100 dark:bg-yellow-900/50 border-l-4 border-l-yellow-500",
      civil_complete:
        "bg-pink-100 dark:bg-pink-900/50 border-l-4 border-l-pink-500",
      technical_pending:
        "bg-yellow-100 dark:bg-yellow-900/50 border-l-4 border-l-yellow-500",
      technical_inprogress:
        "bg-yellow-100 dark:bg-yellow-900/50 border-l-4 border-l-yellow-500",
      technical_complete:
        "bg-green-100 dark:bg-green-900/50 border-l-4 border-l-green-500",
      commissioning_not_started:
        "bg-amber-100 dark:bg-amber-900/50 border-l-4 border-l-amber-500",
      commissioning_inprogress:
        "bg-orange-100 dark:bg-orange-900/50 border-l-4 border-l-orange-500",
      commissioning_complete:
        "bg-emerald-100 dark:bg-emerald-900/50 border-l-4 border-l-emerald-600",
    };
    return (
      rowStyles[status] ||
      "bg-gray-50/50 dark:bg-gray-800/30 border-l-4 border-l-gray-300 dark:border-l-gray-600"
    );
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="opacity-40" />;
    }
    return sortConfig?.direction === "asc" ? (
      <Icon name="ArrowUp" size={14} />
    ) : (
      <Icon name="ArrowDown" size={14} />
    );
  };

  return (
    <div className="w-full overflow-hidden">
      {/* Sort Controls */}
      <div className="mb-4 p-4 bg-muted/30 rounded-lg border border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">Sort by:</span>
            <button
              onClick={() => handleSort("cin")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                sortConfig?.key === "cin"
                  ? "bg-primary/10 text-primary"
                  : "bg-background text-foreground hover:bg-muted border border-border"
              }`}
            >
              CIN
              <SortIcon columnKey="cin" />
            </button>
            <button
              onClick={() => handleSort("usn")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                sortConfig?.key === "usn"
                  ? "bg-primary/10 text-primary"
                  : "bg-background text-foreground hover:bg-muted border border-border"
              }`}
            >
              USN
              <SortIcon columnKey="usn" />
            </button>
            <button
              onClick={() => handleSort("name")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                sortConfig?.key === "name"
                  ? "bg-primary/10 text-primary"
                  : "bg-background text-foreground hover:bg-muted border border-border"
              }`}
            >
              Name
              <SortIcon columnKey="name" />
            </button>
            {sortConfig?.key && (
              <button
                onClick={() => setSortConfig({ key: null, direction: "asc" })}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted border border-border transition-colors"
              >
                Clear Sort
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onExportCustomers && (
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={onExportCustomers}
              >
                Export
              </Button>
            )}
            {onBulkDelete && selectedIds?.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                iconName={isBulkDeleting ? "Loader2" : "Trash2"}
                iconPosition="left"
                iconClassName={isBulkDeleting ? "animate-spin" : ""}
                onClick={onBulkDelete}
                disabled={isBulkDeleting}
              >
                Delete selected ({selectedIds.length})
              </Button>
            )}
            {onDownloadQRCardsPDF && selectedIds?.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={onDownloadQRCardsPDF}
              >
                Download QR ({selectedIds.length})
              </Button>
            )}
            {(onBulkDownloadSurveyReports ||
              onBulkDownloadWorkCompletionReports) &&
              selectedIds?.length > 0 && (
                <div className="relative" ref={reportDropdownRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="FileText"
                    iconPosition="left"
                    onClick={() => setReportDropdownOpen((o) => !o)}
                    className="gap-1"
                  >
                    Download report ({selectedIds.length})
                    <Icon
                      name="ChevronDown"
                      size={14}
                      className={`transition-transform ${reportDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </Button>
                  {reportDropdownOpen && (
                    <div className="absolute right-0 top-full z-[100] mt-1 min-w-[160px] rounded-md border border-border bg-card py-1 shadow-lg">
                      {onBulkDownloadSurveyReports && (
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
                          onClick={() => {
                            onBulkDownloadSurveyReports();
                            setReportDropdownOpen(false);
                          }}
                        >
                          Survey
                        </button>
                      )}
                      {onBulkDownloadWorkCompletionReports && (
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
                          onClick={() => {
                            onBulkDownloadWorkCompletionReports();
                            setReportDropdownOpen(false);
                          }}
                        >
                          WCR
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Select All Checkbox */}
      {customers?.length > 0 && (
        <div className="mb-4 px-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={
                customers?.length > 0 &&
                customers?.every((c) => selectedIds?.includes(c?.id))
              }
              onChange={(e) => {
                if (e?.target?.checked) {
                  const newSelectedIds = [
                    ...selectedIds,
                    ...customers
                      .map((c) => c?.id)
                      .filter((id) => !selectedIds?.includes(id)),
                  ];
                  onSelect(newSelectedIds);
                } else {
                  const customerIds = customers?.map((c) => c?.id);
                  onSelect(
                    selectedIds?.filter((id) => !customerIds?.includes(id)),
                  );
                }
              }}
              className="w-4 h-4 rounded border-border"
            />
            <span className="text-sm text-muted-foreground">
              Select all ({selectedIds?.length || 0} selected)
            </span>
          </label>
        </div>
      )}

      {/* Lean Table View */}
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[1400px]">
          <thead className="bg-muted/40">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground w-[44px]">
                Sel
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                Customer
              </th>
              {!isClusterManager && (
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                  Region
                </th>
              )}
              {!isClusterManager && (
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                  Cluster
                </th>
              )}
              <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                Status
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                Team Member
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                Quick Assign
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedCustomers?.map((customer) => (
              <tr
                key={customer?.id}
                className={`${getRowStatusClasses(
                  customer?.status,
                )} hover:opacity-90 transition-colors`}
              >
                <td className="px-3 py-3 align-top">
                  <input
                    type="checkbox"
                    checked={selectedIds?.includes(customer?.id)}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        onSelect([...selectedIds, customer?.id]);
                      } else {
                        onSelect(
                          selectedIds?.filter((id) => id !== customer?.id),
                        );
                      }
                    }}
                    className="w-4 h-4 rounded border-border cursor-pointer"
                  />
                </td>

                <td className="px-3 py-3 align-top">
                  <div className="min-w-0">
                    <div
                      className="text-sm font-medium text-foreground cursor-pointer hover:text-primary transition-colors truncate"
                      onClick={() =>
                        navigate(`/customer-detail/${customer?.id}`)
                      }
                      title={customer?.name}
                    >
                      {customer?.name || "—"}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground font-mono">
                      <span className="text-foreground">
                        {customer?.cin || "—"}
                      </span>
                      <span className="mx-1.5">•</span>
                      <span className="text-foreground">
                        {customer?.usn || "—"}
                      </span>
                    </div>
                  </div>
                </td>

                {!isClusterManager && (
                  <td className="px-3 py-3 align-top text-sm text-foreground">
                    {customer?.regions?.name || "—"}
                  </td>
                )}

                {!isClusterManager && (
                  <td className="px-3 py-3 align-top">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Select
                        placeholder="Assign Cluster"
                        value={customer?.cluster_id || ""}
                        onChange={async (value) => {
                          if (onAssignCluster) {
                            await onAssignCluster(customer.id, value || null);
                          }
                        }}
                        options={[
                          { value: "", label: "— No Cluster —" },
                          ...clusters.map((cluster) => ({
                            value: cluster.id,
                            label: cluster.name,
                          })),
                        ]}
                        searchable
                        className="text-xs min-w-[200px]"
                        usePortal
                      />
                    </div>
                  </td>
                )}

                <td className="px-3 py-3 align-top">
                  <div className="flex items-center gap-2">
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="min-w-[220px]"
                    >
                      <Select
                        placeholder="Change Status"
                        value={customer?.status || ""}
                        onChange={async (value) => {
                          if (onAssignStatus && value) {
                            await onAssignStatus(customer.id, value);
                          }
                        }}
                        options={statusOptions}
                        searchable
                        className="text-xs"
                        usePortal
                      />
                    </div>
                  </div>
                </td>

                <td className="px-3 py-3 align-top">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Select
                      placeholder="Assign Member"
                      value={customer?.assigned_member_id || ""}
                      onChange={async (value) => {
                        if (onAssignMember) {
                          await onAssignMember(customer.id, value || null);
                        }
                      }}
                      options={[
                        { value: "", label: "— No Member —" },
                        ...getAssignableTeamMembers(customer).map((member) => ({
                          value: member.id,
                          label: member.role
                            ? `${member.name || "—"} (${formatRole(
                                member.role,
                              )})`
                            : member.name || "—",
                        })),
                      ]}
                      searchable
                      className="text-xs min-w-[260px]"
                      disabled={!customer?.team_id}
                      usePortal
                    />
                  </div>
                </td>

                <td className="px-3 py-3 align-top">
                  {(isClusterManager || userRole === "project_admin") && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {customer?.status === "survey_not_started" &&
                        !customer?.assigned_member_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="UserPlus"
                            iconSize={14}
                            onClick={async () => {
                              if (onQuickAssignByRole) {
                                await onQuickAssignByRole(
                                  customer.id,
                                  "worker",
                                );
                              }
                            }}
                            disabled={
                              deletingId === customer?.id || !customer?.team_id
                            }
                            className="text-xs"
                          >
                            Worker
                          </Button>
                        )}

                      {customer?.status === "survey_complete" &&
                        (
                          customer?.survey_approval_status || ""
                        ).toLowerCase() === "approved" && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="UserPlus"
                            iconSize={14}
                            onClick={() =>
                              onOpenAssignRole?.(customer, "civil")
                            }
                            disabled={
                              deletingId === customer?.id || !customer?.team_id
                            }
                            className="text-xs"
                          >
                            Civil
                          </Button>
                        )}

                      {customer?.status === "civil_complete" &&
                        (
                          customer?.civil_approval_status || ""
                        ).toLowerCase() === "approved" && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="UserPlus"
                            iconSize={14}
                            onClick={() =>
                              onOpenAssignRole?.(customer, "technical")
                            }
                            disabled={
                              deletingId === customer?.id || !customer?.team_id
                            }
                            className="text-xs"
                          >
                            Technical
                          </Button>
                        )}
                    </div>
                  )}
                </td>

                <td className="px-3 py-3 align-top">
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="min-w-[220px]"
                  >
                    <Select
                      placeholder="Actions"
                      value={rowActionSelection?.[customer?.id] || ""}
                      onChange={async (value) => {
                        if (!value) return;
                        setRowActionSelection((prev) => ({
                          ...prev,
                          [customer?.id]: value,
                        }));

                        try {
                          switch (value) {
                            case "view_details":
                              navigate(`/customer-detail/${customer?.id}`);
                              break;
                            case "view_qr":
                              if (customer?.qr_code) {
                                setViewingQRCode({
                                  qrCode: customer.qr_code,
                                  customer: customer,
                                });
                              }
                              break;
                            case "download_survey":
                              if (onDownloadSurveyReport) {
                                await onDownloadSurveyReport(customer.id);
                              }
                              break;
                            case "download_work":
                              if (onDownloadWorkCompletionReport) {
                                await onDownloadWorkCompletionReport(
                                  customer.id,
                                );
                              }
                              break;
                            case "download_photos":
                              if (onDownloadPhotos) {
                                await onDownloadPhotos(customer.id);
                              }
                              break;
                            case "edit":
                              onEdit(customer);
                              break;
                            case "delete":
                              onDelete(customer?.id);
                              break;
                            default:
                              break;
                          }
                        } finally {
                          // Reset back to placeholder after action
                          setRowActionSelection((prev) => ({
                            ...prev,
                            [customer?.id]: "",
                          }));
                        }
                      }}
                      options={[
                        { value: "", label: "Select action…" },
                        { value: "view_details", label: "View details" },
                        ...(customer?.qr_code
                          ? [{ value: "view_qr", label: "View QR code" }]
                          : []),
                        ...(customer?.status !== "survey_not_started" &&
                        customer?.status !== "survey_inprogress" &&
                        onDownloadSurveyReport
                          ? [
                              {
                                value: "download_survey",
                                label: "Download survey report",
                              },
                            ]
                          : []),
                        ...((customer?.status === "technical_complete" ||
                          customer?.status === "commissioning_complete") &&
                        onDownloadWorkCompletionReport
                          ? [
                              {
                                value: "download_work",
                                label: "Download WCR",
                              },
                            ]
                          : []),
                        ...(onDownloadPhotos
                          ? [
                              {
                                value: "download_photos",
                                label: "Download photos",
                              },
                            ]
                          : []),
                        { value: "edit", label: "Edit" },
                        { value: "delete", label: "Delete" },
                      ]}
                      className="text-xs"
                      disabled={deletingId === customer?.id}
                      usePortal
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedCustomers?.length === 0 && (
        <div className="p-12 text-center">
          <Icon
            name="Users"
            size={48}
            className="mx-auto mb-4 text-muted-foreground"
          />
          <p className="text-sm text-muted-foreground">No customers found</p>
        </div>
      )}

      {/* QR Code Viewer Modal */}
      <QRCodeViewerModal
        isOpen={!!viewingQRCode}
        onClose={() => setViewingQRCode(null)}
        qrCode={viewingQRCode?.qrCode}
        customer={viewingQRCode?.customer}
        regions={regions}
      />
    </div>
  );
};

export default CustomerTable;
